import React from 'react';
import isEqual from 'react-fast-compare';
import {omit} from 'underscore';

import {SequenceSchemaType, TagSchemaSupportedFolderItemType} from 'constants/RegistryConstants';
import FieldTableRow from 'registry/field/FieldTableRow';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaInterface} from 'schemas/SchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBannerStack from 'ui-library/banners/InlineBannerStack';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';

type SchemaFieldWithOriginalField = SchemaField & {
  originalField: SchemaField;
};

export interface AddSchemaInterfaceModalFieldTableRowProps {
  schemaFieldSelectors: Array<SchemaFieldSelector>;
  ownerUrlId: string;
  id: string;
  initialField: SchemaFieldWithOriginalField;
  onCancel: (id: string) => void;
  onEdit: (id: string) => void;
  requestChange: (id: string, fieldWithChanges: SchemaField) => void;
  requestRemove: (id: string) => void;
  tagSchemas?: Array<TagSchemaSummaryGraphQL>;
  storableSchemas?: Array<StorableSchema>;
  schemaInterfaces?: Array<SchemaInterface>;
  shouldShowIsRequired: boolean;
  submissionWarnings: Array<JSX.Element>;
  fileType?: TagSchemaSupportedFolderItemType | null;
  sequenceType?: SequenceSchemaType | null;
}

function validateFieldTypeChange(
  updatedField: SchemaField,
  originalField: SchemaField
): InlineBannerProps | null {
  if (updatedField.fieldType !== originalField.fieldType) {
    return {
      text: 'Field type changes may result in data loss',
      bannerType: InlineBannerType.WARNING,
    };
  }

  return null;
}

function validateWarehouseChange(
  updatedField: SchemaField,
  originalField: SchemaField
): InlineBannerProps | null {
  if (updatedField.fieldType !== originalField.fieldType || updatedField.isMulti !== originalField.isMulti) {
    return {
      text: 'These changes will temporarily remove data from the warehouse',
      supportingInfo:
        'All fields with type or multi-select changes will be unavailable while we backfill these values. We recommend making these changes on an evening or weekend to minimize disruption',
      bannerType: InlineBannerType.WARNING,
    };
  }

  return null;
}

function validateRequiredChange(
  updatedField: SchemaField,
  originalField: SchemaField
): InlineBannerProps | null {
  if (updatedField.isRequired !== originalField.isRequired) {
    if (updatedField.isRequired) {
      return {
        text: 'Changing field to required may result in invalid entities',
        supportingInfo: 'Existing entities with empty values will be flagged as invalid',
        bannerType: InlineBannerType.WARNING,
      };
    } else {
      return {
        text: 'Changing field to not required',
        bannerType: InlineBannerType.WARNING,
      };
    }
  }

  return null;
}

function validateMultiChange(
  updatedField: SchemaField,
  originalField: SchemaField
): InlineBannerProps | null {
  if (updatedField.isMulti !== originalField.isMulti) {
    if (!updatedField.isMulti) {
      return {
        text: 'Changing field from multi-value to single-value may result in data loss',
        supportingInfo: 'Only the first value in each field will be preserved',
        bannerType: InlineBannerType.WARNING,
      };
    } else {
      return {
        text: 'Changing field from single-value to multi-value',
        bannerType: InlineBannerType.WARNING,
      };
    }
  }

  return null;
}

function validateParentLinkChange(
  updatedField: SchemaField,
  originalField: SchemaField
): InlineBannerProps | null {
  if (updatedField.isParentLink !== originalField.isParentLink) {
    return {
      text: 'Changing field parent link',
      bannerType: InlineBannerType.WARNING,
    };
  }

  return null;
}

function validateSystemNameChange(
  updatedField: SchemaField,
  originalField: SchemaField
): InlineBannerProps | null {
  if (originalField.systemName !== '' && updatedField.systemName !== originalField.systemName) {
    return {
      text: 'Changing system name may break warehouse connections - e.g. dashboards and API integrations',
      supportingInfo: 'We recommend consulting your development or IT staff before making this change',
      bannerType: InlineBannerType.WARNING,
    };
  }

  return null;
}

function validateDisplayNameChange(
  updatedField: SchemaField,
  originalField: SchemaField
): InlineBannerProps | null {
  if (updatedField.displayName !== originalField.displayName) {
    return {
      text: 'Changing field display name',
      bannerType: InlineBannerType.WARNING,
    };
  }

  return null;
}

function validateComputedChange(
  updatedField: SchemaField,
  originalField: SchemaField
): InlineBannerProps | null {
  if (updatedField.isComputed !== originalField.isComputed) {
    return {
      text: updatedField.isComputed
        ? 'Cannot change user-entered field to computed field'
        : 'Cannot change computed field to user-entered field',
      bannerType: InlineBannerType.ERROR,
    };
  }

  return null;
}

function validateRequiredLinkChange(
  updatedField: SchemaField,
  originalField: SchemaField
): InlineBannerProps | null {
  // Coalesce fields to null if empty to avoid comparing undefined and null
  if (!isEqual(updatedField.requiredLink ?? null, originalField.requiredLink ?? null)) {
    return {
      text: 'Changing field entity link type',
      bannerType: InlineBannerType.WARNING,
    };
  }

  return null;
}

function validateSchemaFieldSelectorChange(
  updatedField: SchemaField,
  originalField: SchemaField
): InlineBannerProps | null {
  // Coalesce fields to null if empty to avoid comparing undefined and null
  if (!isEqual(updatedField.schemaFieldSelectorId ?? null, originalField.schemaFieldSelectorId ?? null)) {
    return {
      text: 'Dropdown changes may result in data loss',
      bannerType: InlineBannerType.WARNING,
    };
  }

  return null;
}

function validateArchiveRecordChange(
  updatedField: SchemaField,
  originalField: SchemaField
): InlineBannerProps | null {
  if (Boolean(updatedField.archiveRecord) !== Boolean(originalField.archiveRecord)) {
    const isBeingUnarchived = updatedField.archiveRecord == null;
    const supportingInfo = isBeingUnarchived
      ? 'This field will be unarchived. If you want a completely new field, please cancel this and first rename the existing archived field.'
      : 'This field will be archived';

    return {
      text: 'Changing field archival status',
      bannerType: InlineBannerType.WARNING,
      supportingInfo,
    };
  }

  return null;
}

export const fieldUpdateValidators: Array<
  (updatedField: SchemaField, originalField: SchemaField) => InlineBannerProps | null
> = [
  validateFieldTypeChange,
  validateWarehouseChange,
  validateRequiredChange,
  validateMultiChange,
  validateParentLinkChange,
  validateSystemNameChange,
  validateDisplayNameChange,
  validateComputedChange,
  validateRequiredLinkChange,
  validateSchemaFieldSelectorChange,
  validateArchiveRecordChange,
];

/** This component will diff two schema fields and show warnings / errors for what has changed. */
export default function AddSchemaInterfaceModalFieldTableRow(
  props: AddSchemaInterfaceModalFieldTableRowProps
): JSX.Element {
  /** The updated field is the schema interface field we are trying to match. */
  const updatedField = omit(props.initialField, 'originalField');
  /** The original field is the current field on the tag schema. */
  const originalField = props.initialField.originalField;
  const messages: Array<InlineBannerProps> = [];
  if (originalField != null) {
    fieldUpdateValidators.forEach((validator) => {
      const message = validator(updatedField, originalField);
      if (message != null) {
        messages.push(message);
      }
    });
  }

  return (
    <>
      <FieldTableRow
        availableFieldOptions={[]}
        {...props}
        initialField={updatedField}
        isInEditMode={false}
        isUneditable={false}
        disableDrag={true}
        shouldShowWarehouseWarning={true}
        schemaId={null}
        disabled={true}
      />
      <InlineBannerStack messages={messages} />
    </>
  );
}
